import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSingleRecipeGroup,
  getSingleRecipeGroupLoading,
  removeRecipesGroups
} from './redux/selectors';
import { get_single_recipe_group, remove_recipe_group } from './redux/reducer';
import LoadingDataUi from '@app/components/loading';
import CreateRecipeGroup from './blocks/create-recipe-group';
import ConfirmDeleteModal from './blocks/confirm-delete-modal';
import { MdChevronLeft, MdEdit, MdDeleteOutline } from 'react-icons/md';
import commaNumber from 'comma-number';

const mealTypeColors = {
  Breakfast: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200'
  },
  Lunch: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200'
  },
  Dinner: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200'
  }
};

const RecipeGroupDetail = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const group = useSelector(getSingleRecipeGroup);
  const loading = useSelector(getSingleRecipeGroupLoading);
  const isDeleting = useSelector(removeRecipesGroups);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    dispatch(get_single_recipe_group(params.id));
  }, [dispatch, params.id]);

  const handleDelete = () => {
    dispatch(remove_recipe_group(group._id));
    setTimeout(() => {
      window.location.href = '/recipe-groups';
    }, 1000);
  };

  if (loading || !group) {
    return <LoadingDataUi />;
  }

  const colors = (type) =>
    mealTypeColors[type] || {
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      border: 'border-gray-200'
    };

  return (
    <div>
      <CreateRecipeGroup
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        content={group}
        isEdit={true}
      />

      <ConfirmDeleteModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        itemName={group.name}
        isDeleting={isDeleting}
      />

      {/* Header */}
      <div className="px-5 py-6 border-b border-[#DEE5ED]">
        <Link
          to="/recipe-groups"
          className="inline-flex items-center text-sm text-[#7C8091] hover:text-[#14151A] transition-colors mb-3"
        >
          <MdChevronLeft size={20} />
          Back to Recipe Groups
        </Link>
        <div className="flex justify-between items-center">
          <h1 className="text-[32px] font-bold capitalize">{group.name}</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setEditOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-[8px] bg-[#003333] text-white text-sm font-medium hover:bg-[#004444] transition-colors"
            >
              <MdEdit size={16} />
              Edit
            </button>
            <button
              onClick={() => setDeleteOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-[8px] bg-[#E6483D] text-white text-sm font-medium hover:bg-red-600 transition-colors"
            >
              <MdDeleteOutline size={16} />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="grid grid-cols-3 gap-6">
          {/* Left column - Image */}
          <div className="col-span-1">
            <img
              src={group.image}
              alt={group.name}
              className="w-full rounded-[16px] object-cover aspect-square"
            />
          </div>

          {/* Right column - Details */}
          <div className="col-span-2 space-y-6">
            {/* Description */}
            {group.description && (
              <div>
                <h3 className="text-sm font-medium text-[#7C8091] mb-1">
                  Description
                </h3>
                <p className="text-[#14151A]">{group.description}</p>
              </div>
            )}

            {/* Meal Types */}
            <div>
              <h3 className="text-sm font-medium text-[#7C8091] mb-2">Meal Types</h3>
              <div className="flex flex-wrap gap-2">
                {group.meal_types.map((type) => (
                  <span
                    key={type}
                    className={`text-sm font-medium px-3 py-1.5 rounded-full border ${
                      colors(type).bg
                    } ${colors(type).text} ${colors(type).border}`}
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#F8F9FA] rounded-[12px] p-4">
                <p className="text-sm text-[#7C8091]">Plan</p>
                <p className="text-lg font-semibold text-[#14151A] mt-1">
                  {group.plan}
                </p>
              </div>
              <div className="bg-[#F8F9FA] rounded-[12px] p-4">
                <p className="text-sm text-[#7C8091]">Servings</p>
                <p className="text-lg font-semibold text-[#14151A] mt-1">
                  {group.servings}
                </p>
              </div>
              <div className="bg-[#F8F9FA] rounded-[12px] p-4">
                <p className="text-sm text-[#7C8091]">Price</p>
                <p className="text-lg font-semibold text-[#14151A] mt-1">
                  {group.price?.currency?.symbol} {commaNumber(group.price?.value)}
                </p>
              </div>
            </div>

            {/* Benefits */}
            {group.benefits && group.benefits.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-[#7C8091] mb-2">Benefits</h3>
                <ul className="space-y-2">
                  {group.benefits.map((benefit, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-[#14151A] text-sm"
                    >
                      <span className="mt-1.5 size-1.5 rounded-full bg-[#549A9A] flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recipes */}
            {group.recipes && group.recipes.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-[#7C8091] mb-2">
                  Recipes ({group.recipes.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {group.recipes.map((recipe) => (
                    <span
                      key={recipe._id}
                      className="text-sm bg-[#F0F7F7] text-[#003333] px-3 py-1.5 rounded-[8px] border border-[#ACD1D1] capitalize"
                    >
                      {recipe.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeGroupDetail;
